// Vocabulario visual de la pantalla de detalle.
//
// Dos registros a propósito, y no se mezclan:
//   · BENTO — sólo el resumen de arriba. Bloques con marco, de tres tamaños,
//     para leer el estado del pedido de un vistazo.
//   · SECCIÓN — de Cargas hacia abajo. Nada de tarjetas: un título discreto y el
//     contenido, separados del bloque anterior por un borde. Es zona de trabajo
//     y lo que importa es la densidad, no el envoltorio.
//
// Sin sombras, sin gradientes y sin glassmorphism; la única excepción es el velo
// de la barra de navegación (`navStickyCls`), que lo necesita para que el
// contenido no se le pegue al pasar por debajo.

/** Superficie de un bloque del bento. */
export const bloqueCls = (dark: boolean, protagonista = false) =>
  [
    'rounded-xl border p-4',
    dark
      ? `border-white/8 ${protagonista ? 'bg-white/[0.05]' : 'bg-white/[0.03]'}`
      : `border-slate-200 ${protagonista ? 'bg-slate-50/60' : 'bg-white'}`,
  ].join(' ')

/** Etiqueta de un bloque del bento o título de una sección. */
export const rotuloCls = (dark: boolean) =>
  `text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`

/** Cifra protagonista. */
export const cifraGrandeCls = (dark: boolean) =>
  `text-[26px] font-black tabular-nums leading-none ${dark ? 'text-slate-100' : 'text-slate-900'}`

/** Cifra secundaria dentro de un bloque. */
export const cifraCls = (dark: boolean) =>
  `text-[15px] font-bold tabular-nums ${dark ? 'text-slate-100' : 'text-slate-900'}`

export const textoCls = (dark: boolean) => (dark ? 'text-slate-200' : 'text-slate-700')
export const apagadoCls = (dark: boolean) => (dark ? 'text-slate-400' : 'text-slate-500')
export const tenueCls = (dark: boolean) => (dark ? 'text-slate-600' : 'text-slate-400')
export const bordeCls = (dark: boolean) => (dark ? 'border-white/8' : 'border-slate-200')
export const bordeTenueCls = (dark: boolean) => (dark ? 'border-white/4' : 'border-slate-100')

/** Barra de navegación interna: el único sitio con velo. */
export const navStickyCls = (dark: boolean) =>
  [
    'sticky top-0 z-20 -mx-6 px-6 border-b backdrop-blur-md',
    dark ? 'border-white/8 bg-slate-950/80' : 'border-slate-200 bg-white/85',
  ].join(' ')

/** Botón neutro, el de la toolbar de todo el repo. */
export const btnNeutroCls = (dark: boolean) =>
  [
    'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer',
    dark
      ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
  ].join(' ')

/** Botón de acción principal. */
export const btnPrimarioCls =
  'px-3 min-h-[34px] rounded-lg text-[12px] font-semibold bg-sky-600 text-white ' +
  'hover:bg-sky-500 transition-colors cursor-pointer'

/** Píldora de filtro rápido (cargas). */
export const pildoraCls = (dark: boolean, activa: boolean) =>
  [
    'text-[11px] px-2.5 py-1 rounded-full border transition-colors font-medium cursor-pointer',
    activa
      ? dark
        ? 'border-sky-500/40 bg-sky-500/15 text-sky-300'
        : 'border-sky-200 bg-sky-50 text-sky-700'
      : dark
        ? 'border-white/10 bg-transparent text-slate-400 hover:bg-white/5'
        : 'border-slate-200 bg-transparent text-slate-500 hover:bg-slate-50',
  ].join(' ')

/** Distintivo de aviso, en dos intensidades.
 *
 *  `fuerte` va en color plano y texto blanco: es para lo que el usuario tiene
 *  que ver ANTES de leer nada —una entrega vencida, un pedido sin consolidar—.
 *  El tinte translúcido de antes se perdía junto a un número de 30 px, y ese
 *  aviso es justo el que no se puede perder.
 *
 *  `suave` es el registro normal, para lo que informa sin exigir una acción.
 *  Si todo fuera fuerte, nada lo sería. */
export const avisoCls = (
  tono: 'rojo' | 'ambar',
  intensidad: 'fuerte' | 'suave',
  dark: boolean,
) => {
  const base = 'inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-[3px] rounded-md'
  if (intensidad === 'fuerte') {
    return `${base} text-white ${tono === 'rojo' ? 'bg-red-600' : 'bg-amber-600'}`
  }
  if (tono === 'rojo') {
    return `${base} ${dark ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-700'}`
  }
  return `${base} ${dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700'}`
}

/** Celda de cabecera de tabla densa. */
export const thCls = (dark: boolean) =>
  `px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
    dark ? 'text-slate-500' : 'text-slate-400'
  }`

/** Celda de cuerpo de tabla densa. */
export const tdCls = (dark: boolean) =>
  `px-2 py-1.5 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
